import { Alert, EmptyState, SkeletonRows } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import { InvoiceForm } from "../../../../features/invoicing/invoice-form.js";
import {
  blankReceiptRow,
  headerStateFromInvoice,
  lineStateFromInvoiceLine,
} from "../../../../features/invoicing/invoice-values.js";
import { useInvoice } from "../../../../features/invoicing/queries.js";
import {
  InvoicingPage,
  useInvoicingCrumb,
  useInvoicingPermissions,
} from "../../../../features/invoicing/shared.js";
import { errorMessage } from "../../../../lib/errors.js";

export const Route = createFileRoute("/_app/faturalar/$id/duzenle")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: EditInvoicePage,
});

function EditInvoicePage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const crumb = useInvoicingCrumb("/faturalar");
  const permissions = useInvoicingPermissions();
  const detail = useInvoice(id);
  const invoice = detail.data;
  const title = invoice?.invoiceNo ?? t("invoicing.detail.draftTitle");

  return (
    <InvoicingPage
      title={t("invoicing.edit.title")}
      description={t("invoicing.edit.description")}
      breadcrumb={[crumb, { label: title }, { label: t("common.edit") }]}
    >
      {({ companyId }) => {
        if (detail.error) return <Alert tone="danger">{errorMessage(detail.error)}</Alert>;
        if (!invoice) return <SkeletonRows rows={8} />;
        const canWrite =
          invoice.direction === "SALES" ? permissions.canManageSales : permissions.canManagePurchase;
        if (invoice.status !== "DRAFT" || !canWrite) {
          return (
            <EmptyState
              headingLevel={2}
              icon={<ShieldAlert />}
              title={
                invoice.status === "DRAFT"
                  ? t("invoicing.form.noPermission")
                  : t("invoicing.edit.notDraftTitle")
              }
              description={
                invoice.status === "DRAFT"
                  ? t("invoicing.form.noPermissionBody")
                  : t("invoicing.edit.notDraftBody")
              }
            />
          );
        }
        return (
          <InvoiceForm
            companyId={companyId}
            initialHeader={headerStateFromInvoice(invoice)}
            initialLines={invoice.lines.map(lineStateFromInvoiceLine)}
            initialReceipts={invoice.receipts.map((receipt) => ({
              ...blankReceiptRow(receipt.stationId),
              fiscalDeviceSerial: receipt.fiscalDeviceSerial,
              zNo: String(receipt.zNo),
              receiptNo: String(receipt.receiptNo),
              receiptDate: receipt.receiptDate,
              receiptTime: receipt.receiptTime ?? "",
              amount: receipt.amount,
            }))}
            saved={{ id: invoice.id, version: invoice.version }}
            lockKind
          />
        );
      }}
    </InvoicingPage>
  );
}
